<script setup lang="ts">
/** Connected apps: MCP clients the signed-in user allowed to act as them (consents) — see and disconnect. */
import { AppWindow, Unplug } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, h } from "vue";
import { toast } from "vue-sonner";
import { clientName, disconnectApp, useConnectedApps, type ConnectedApp } from "@/api/oauth";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { oauth } from "@/copy/oauth";
import { errorMessage } from "@/lib/errors";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "Connected apps" } });

const c = oauth.connectedApps;
const queryClient = useQueryClient();
const apps = useConnectedApps();

async function onDisconnect(app: ConnectedApp) {
  const name = clientName(app);
  const answer = await confirm({ title: `${c.disconnect} ${name}?`, description: c.disconnectConfirm(name), confirmLabel: c.disconnect, destructive: true });
  if (!answer) return;
  try {
    await disconnectApp(app.consentId);
    toast.success(c.disconnected);
    await queryClient.invalidateQueries({ queryKey: ["me", "connected-apps"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const columns = computed<Column<ConnectedApp>[]>(() => [
  {
    id: "app",
    header: c.columns.app,
    accessorFn: (a) => `${clientName(a)} ${a.clientId}`,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "min-w-0 max-w-md whitespace-normal" }, [
        h("p", { class: "flex flex-wrap items-center gap-1.5 font-medium break-words" }, [
          clientName(row.original),
          row.original.kind === "dcr" ? h(Badge, { variant: "warning" }, () => oauth.kind.dcr) : h(Badge, { variant: "muted" }, () => oauth.kind[row.original.kind]),
          row.original.clientStatus !== "active" ? h(Badge, { variant: "danger" }, () => oauth.status[row.original.clientStatus]) : null,
        ]),
        row.original.kind === "cimd" ? h("p", { class: "mt-0.5 truncate font-mono text-xs font-normal text-muted-foreground", title: row.original.clientId }, row.original.clientId) : null,
      ]),
  },
  { id: "returns", header: c.columns.returnsTo, accessorFn: (a) => a.redirectLabel, cell: ({ row }) => h("span", { class: "block max-w-xs text-sm whitespace-normal", title: row.original.redirectUri }, row.original.redirectLabel) },
  { id: "consented", header: c.columns.connected, accessorFn: (a) => a.consentedAt, cell: ({ row }) => h(RelativeTime, { value: row.original.consentedAt, class: "text-sm whitespace-nowrap" }) },
  {
    id: "used",
    header: c.columns.lastUsed,
    accessorFn: (a) => a.lastUsedAt ?? "",
    cell: ({ row }) => (row.original.lastUsedAt ? h(RelativeTime, { value: row.original.lastUsedAt, class: "text-sm whitespace-nowrap" }) : h("span", { class: "text-sm text-muted-foreground" }, c.never)),
  },
  {
    id: "actions",
    header: "",
    enableSorting: false,
    meta: { mobile: "actions", align: "end" },
    cell: ({ row }) =>
      h(Button, { size: "sm", variant: "ghost", class: "text-destructive", "aria-label": `${c.disconnect} ${clientName(row.original)}`, onClick: () => onDisconnect(row.original) }, () => [h(Unplug), c.disconnect]),
  },
]);
</script>

<template>
  <div>
    <PageHeader :title="c.title" :description="c.description" />
    <ListSkeleton v-if="apps.isPending.value" :rows="3" />
    <ErrorState v-else-if="apps.error.value" :error="apps.error.value" @retry="apps.refetch()" />
    <EmptyState v-else-if="!apps.data.value?.length" :icon="AppWindow" :title="c.empty" :description="c.emptyHint" />
    <DataTable v-else :data="apps.data.value" :columns="columns" :row-key="(a) => a.consentId" :caption="c.title" />
  </div>
</template>
